// Sistema de Analytics para el Portfolio Terminal

export const trackVisitor = () => {
  const now = new Date().toISOString();
  
  // Obtener datos existentes
  const analytics = JSON.parse(localStorage.getItem('portfolioAnalytics') || '{}');
  
  // Inicializar si no existe
  if (!analytics.totalVisits) {
    analytics.totalVisits = 0;
    analytics.uniqueVisitors = 0;
    analytics.firstVisit = now;
    analytics.visits = [];
  }
  
  // Incrementar visitas totales
  analytics.totalVisits++;
  
  // Detectar si es visitante único (primera visita o después de 30 días)
  const lastVisit = analytics.lastVisit ? new Date(analytics.lastVisit) : null;
  const daysSinceLastVisit = lastVisit ? (new Date() - lastVisit) / (1000 * 60 * 60 * 24) : null;
  
  if (!lastVisit || daysSinceLastVisit > 30) {
    analytics.uniqueVisitors++;
  }
  
  // Actualizar última visita
  analytics.lastVisit = now;
  
  // Guardar registro de visita (mantener últimas 100)
  analytics.visits.push({
    timestamp: now,
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`
  });
  
  if (analytics.visits.length > 100) {
    analytics.visits = analytics.visits.slice(-100);
  }
  
  // Guardar en localStorage
  localStorage.setItem('portfolioAnalytics', JSON.stringify(analytics));
  
  return analytics;
};

export const getAnalytics = () => {
  const analytics = JSON.parse(localStorage.getItem('portfolioAnalytics') || '{}');
  
  if (!analytics.totalVisits) {
    return {
      totalVisits: 0,
      uniqueVisitors: 0,
      firstVisit: null,
      lastVisit: null,
      visits: []
    };
  }
  
  return analytics;
};

export const formatAnalyticsReport = (language = 'es') => {
  const analytics = getAnalytics();
  
  if (analytics.totalVisits === 0) {
    return language === 'es' 
      ? 'No hay datos de visitas aún.'
      : 'No visit data yet.';
  }
  
  const firstVisit = new Date(analytics.firstVisit);
  const lastVisit = new Date(analytics.lastVisit);
  const daysSinceFirst = Math.floor((new Date() - firstVisit) / (1000 * 60 * 60 * 24));
  
  // Calcular visitas por día
  const visitsPerDay = daysSinceFirst > 0 ? (analytics.totalVisits / daysSinceFirst).toFixed(2) : analytics.totalVisits;
  
  // Obtener visitas de hoy
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const visitsToday = analytics.visits.filter(v => new Date(v.timestamp) >= today).length;
  
  // Obtener visitas de esta semana
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const visitsThisWeek = analytics.visits.filter(v => new Date(v.timestamp) >= weekAgo).length;
  
  // Obtener navegadores más usados
  const browsers = {};
  analytics.visits.forEach(v => {
    const ua = v.userAgent.toLowerCase();
    let browser = 'Other';
    if (ua.includes('chrome')) browser = 'Chrome';
    else if (ua.includes('firefox')) browser = 'Firefox';
    else if (ua.includes('safari')) browser = 'Safari';
    else if (ua.includes('edge')) browser = 'Edge';
    browsers[browser] = (browsers[browser] || 0) + 1;
  });
  
  const topBrowser = Object.entries(browsers).sort((a, b) => b[1] - a[1])[0];
  
  if (language === 'es') {
    return `
╔════════════════════════════════════════════════╗
║        ESTADÍSTICAS DEL PORTFOLIO              ║
╚════════════════════════════════════════════════╝

📊 RESUMEN GENERAL
├─ Visitas totales:        ${analytics.totalVisits}
├─ Visitantes únicos:      ${analytics.uniqueVisitors}
├─ Promedio por día:       ${visitsPerDay}
└─ Días en línea:          ${daysSinceFirst}

📅 VISITAS RECIENTES
├─ Hoy:                    ${visitsToday}
├─ Esta semana:            ${visitsThisWeek}
├─ Primera visita:         ${firstVisit.toLocaleString('es-ES')}
└─ Última visita:          ${lastVisit.toLocaleString('es-ES')}

🌐 NAVEGADORES
└─ Más usado:              ${topBrowser ? `${topBrowser[0]} (${topBrowser[1]} visitas)` : 'N/A'}

💡 Tip: Usa 'stats --detailed' para ver más información
    `;
  } else {
    return `
╔════════════════════════════════════════════════╗
║         PORTFOLIO STATISTICS                   ║
╚════════════════════════════════════════════════╝

📊 GENERAL SUMMARY
├─ Total visits:           ${analytics.totalVisits}
├─ Unique visitors:        ${analytics.uniqueVisitors}
├─ Average per day:        ${visitsPerDay}
└─ Days online:            ${daysSinceFirst}

📅 RECENT VISITS
├─ Today:                  ${visitsToday}
├─ This week:              ${visitsThisWeek}
├─ First visit:            ${firstVisit.toLocaleString('en-US')}
└─ Last visit:             ${lastVisit.toLocaleString('en-US')}

🌐 BROWSERS
└─ Most used:              ${topBrowser ? `${topBrowser[0]} (${topBrowser[1]} visits)` : 'N/A'}

💡 Tip: Use 'stats --detailed' for more information
    `;
  }
};

export const formatDetailedAnalytics = (language = 'es') => {
  const analytics = getAnalytics();
  
  if (analytics.totalVisits === 0) {
    return language === 'es' 
      ? 'No hay datos de visitas aún.'
      : 'No visit data yet.';
  }
  
  // Últimas 10 visitas
  const recentVisits = analytics.visits.slice(-10).reverse();
  
  let report = language === 'es' 
    ? '\n📋 ÚLTIMAS 10 VISITAS:\n\n'
    : '\n📋 LAST 10 VISITS:\n\n';
  
  recentVisits.forEach((visit, index) => {
    const date = new Date(visit.timestamp);
    const browser = visit.userAgent.toLowerCase().includes('chrome') ? 'Chrome' :
                    visit.userAgent.toLowerCase().includes('firefox') ? 'Firefox' :
                    visit.userAgent.toLowerCase().includes('safari') ? 'Safari' :
                    visit.userAgent.toLowerCase().includes('edge') ? 'Edge' : 'Other';
    
    report += `${index + 1}. ${date.toLocaleString(language === 'es' ? 'es-ES' : 'en-US')}\n`;
    report += `   └─ ${browser} | ${visit.screenResolution} | ${visit.language}\n\n`;
  });
  
  return report;
};

export const resetAnalytics = () => {
  localStorage.removeItem('portfolioAnalytics');
  return true;
};
